import { types, flow, applySnapshot } from 'mobx-state-tree';
import debounce from 'lodash/debounce';

import api from 'services/API';
import { Condition } from './types';

import { ALARMS_TYPES, CONDITION_OFFLINE_TYPES } from 'config/alarmsType';

export const SYSTEM_STATUSES = {
  online: 'System Online',
  restored: 'System Restored',
  offline: 'System Offline',
  partial: 'Partial System Offline',
};

export const hardwareStatusInitialState = {
  previousSystemStatus: SYSTEM_STATUSES.online,
  systemStatus: SYSTEM_STATUSES.online,
  offlineConditions: [],
};
let timer = null;

export const hardwareStatusModel = types
  .model({
    previousSystemStatus: types.enumeration('systemStatus', Object.values(SYSTEM_STATUSES)),
    systemStatus: types.enumeration('systemStatus', Object.values(SYSTEM_STATUSES)),
    offlineConditions: types.array(Condition),
  })
  .views(self => ({
    get isOffline() {
      return [SYSTEM_STATUSES.offline, SYSTEM_STATUSES.partial].includes(self.systemStatus);
    },
    get isFullyOffline() {
      return self.systemStatus === SYSTEM_STATUSES.offline;
    },
    get isPartiallyOffline() {
      return self.systemStatus === SYSTEM_STATUSES.partial;
    },
    get isRestored() {
      return self.systemStatus === SYSTEM_STATUSES.restored;
    },
    get isOnline() {
      return self.systemStatus === SYSTEM_STATUSES.online;
    },
    get isPartiallyRestored() {
      return (
        self.previousSystemStatus === SYSTEM_STATUSES.offline &&
        self.systemStatus === SYSTEM_STATUSES.partial
      );
    },

    get offlineGatewaysIds() {
      return self.offlineConditions
        .filter(({ type }) => type === ALARMS_TYPES.SYSTEM)
        .flatMap(({ context }) =>
          Object.keys(context.offline_gateways)
            .filter(id => !context.offline_gateways[id].resolved)
            .map(Number),
        );
    },

    get offlineFlowSensorsIds() {
      return self.offlineConditions
        .filter(({ type }) => type === ALARMS_TYPES.FLOW_SENSOR_S)
        .flatMap(({ context }) =>
          Object.keys(context.offline_sensors)
            .filter(id => !context.offline_sensors[id].resolved)
            .map(Number),
        );
    },
    get offlineTemperatureSensorsIds() {
      return self.offlineConditions
        .filter(({ type }) => type === ALARMS_TYPES.TEMP_SENSOR_S)
        .flatMap(({ context }) =>
          Object.keys(context.offline_sensors)
            .filter(id => !context.offline_sensors[id].resolved)
            .map(Number),
        );
    },
  }))
  .actions(self => ({
    checkHardwareStatus: flow(function* () {
      try {
        yield api.getHardwareStatus();
      } catch (error) {
        console.error(error);
        return Promise.reject(error);
      }
    }),
    setSystemStatus(status, force = false) {
      if (!Object.values(SYSTEM_STATUSES).includes(status)) {
        console.error('[HardwareStatusStore] Trying to set invalid status', status);
        return;
      }

      if (self.systemStatus === status) return;

      self.previousSystemStatus = self.systemStatus;

      if (force) {
        self.systemStatus = status;
        return;
      }

      if (status === SYSTEM_STATUSES.online) {
        if (self.isOffline) {
          self.systemStatus = SYSTEM_STATUSES.restored;

          timer = setTimeout(() => {
            if (self.isRestored) {
              self.setSystemStatus(SYSTEM_STATUSES.online);
            }
          }, 5000);
        }
      } else {
        clearTimeout(timer);

        self.systemStatus = status;
      }
    },

    refreshSystemStatus() {
      if (self.offlineConditions.some(({ type }) => ALARMS_TYPES.SYSTEM === type)) {
        self.setSystemStatus(SYSTEM_STATUSES.offline);
      } else if (
        self.offlineConditions.some(
          ({ type }) => ALARMS_TYPES.FLOW_SENSOR_S === type || ALARMS_TYPES.TEMP_SENSOR_S === type,
        )
      ) {
        self.setSystemStatus(SYSTEM_STATUSES.partial);
      } else {
        self.setSystemStatus(SYSTEM_STATUSES.online);
      }
    },

    setOfflineConditions(conditions) {
      const filteredConditions = conditions.filter(
        ({ type, happened_to }) => CONDITION_OFFLINE_TYPES.includes(type) && happened_to === null,
      );

      applySnapshot(self.offlineConditions, filteredConditions);
      self.refreshSystemStatus();
    },

    upsertOfflineCondition(condition) {
      if (!CONDITION_OFFLINE_TYPES.includes(condition.type)) return;

      const index = self.offlineConditions.findIndex(c => c.id === condition.id);

      if (index === -1) {
        if (condition.happened_to === null) {
          self.offlineConditions.push(condition);
        }
      } else {
        if (condition.happened_to === null) {
          self.offlineConditions[index] = condition;
        } else {
          self.offlineConditions = self.offlineConditions.filter(c => c.id !== condition.id);
        }
      }

      const refresh = debounce(() => {
        self.refreshSystemStatus();
      }, 1000);

      refresh();
    },
  }));
